import { useId } from "react";
import type { ReactElement } from "react";
import { avatarColor, teammateFace } from "../../utils";
import type { HeadShape } from "../../utils";
import type { AvatarIdentity } from "../../types/teammates";
import TeammateLivelyEyes from "./TeammateLivelyEyes";

const SIZES = {
  xs: "h-7 w-7",
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-14 w-14",
  xl: "h-20 w-20",
} as const;

const BODIES: { shape: ReactElement; head: HeadShape }[] = [
  { shape: <circle cx="20" cy="21" r="16" />, head: { cx: 20, cy: 21, rx: 16, ry: 16 } },
  { shape: <rect x="5" y="6" width="30" height="29" rx="11" />, head: { cx: 20, cy: 20.5, rx: 15, ry: 14.5 } },
  { shape: <path d="M21 4c9 0 15 7 15 16s-6 17-16 17S4 31 4 22 11 4 21 4z" />, head: { cx: 20, cy: 20.5, rx: 16, ry: 16.5 } },
  { shape: <path d="M20 3c7 7 15 12 15 21a15 13 0 0 1-30 0c0-9 8-14 15-21z" />, head: { cx: 20, cy: 23, rx: 15, ry: 14 } },
  { shape: <rect x="8" y="4" width="24" height="33" rx="12" />, head: { cx: 20, cy: 20.5, rx: 12, ry: 16.5 } },
  { shape: <path d="M20 5c3 0 5 2 7 5l8 17c2 4-1 8-5 8H10c-4 0-7-4-5-8l8-17c2-3 4-5 7-5z" />, head: { cx: 20, cy: 22, rx: 15, ry: 13 } },
];

type TeammateAvatarProps = {
  teammate: AvatarIdentity;
  size?: keyof typeof SIZES;
  working?: boolean;
  ringClassName?: string;
};

export default function TeammateAvatar({
  teammate,
  size = "sm",
  working = false,
  ringClassName = "ring-white",
}: TeammateAvatarProps) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const face = teammateFace(teammate.name, teammate.appearance);
  const body = BODIES[face.shape % BODIES.length];
  const small = size === "xs" || size === "sm";
  const scale = small ? (face.dotEyes ? 1.3 : 1.12) : 1;
  const eyes = [20 - face.eyeGap, 20 + face.eyeGap];
  const pupilMotion = working ? "motion-safe:animate-teammate-look" : "";
  const lively = (size === "lg" || size === "xl") && !working;

  const eyeArt = face.dotEyes ? (
    <g className={pupilMotion}>
      {eyes.map((cx) => (
        <g key={cx}>
          <circle
            cx={cx + face.lookX * 0.6}
            cy={face.eyeY + face.lookY * 0.6}
            r={2.3 * scale}
            className="fill-gray-800"
          />
          <circle
            cx={cx + face.lookX * 0.6 - 0.7 * scale}
            cy={face.eyeY + face.lookY * 0.6 - 0.8 * scale}
            r={0.7 * scale}
            className="fill-white"
          />
        </g>
      ))}
    </g>
  ) : (
    <>
      {eyes.map((cx) => (
        <ellipse key={cx} cx={cx} cy={face.eyeY} rx={3.5 * scale} ry={4.1 * scale} className="fill-white" />
      ))}
      <g className={pupilMotion}>
        {eyes.map((cx) => (
          <circle
            key={cx}
            cx={cx + face.lookX * 1.2}
            cy={face.eyeY + face.lookY * 1.2 + 0.4}
            r={1.9 * scale}
            className="fill-gray-800"
          />
        ))}
      </g>
    </>
  );

  return (
    <div className="relative shrink-0">
      <svg viewBox="0 0 40 40" aria-hidden="true" className={`${SIZES[size]} drop-shadow-sm`}>
        <defs>
          <radialGradient id={`${id}-shade`} cx="0.34" cy="0.28" r="0.8">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.3" />
            <stop offset="0.45" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="1" stopColor="#0f172a" stopOpacity="0.3" />
          </radialGradient>
          <clipPath id={`${id}-clip`}>{body.shape}</clipPath>
        </defs>
        <g className={working ? "motion-safe:animate-teammate-bob" : undefined}>
          <g className={avatarColor(teammate).fill}>{body.shape}</g>
          <g fill={`url(#${id}-shade)`}>{body.shape}</g>
          <ellipse cx="13" cy="11" rx="5" ry="3" transform="rotate(-25 13 11)" className="fill-white opacity-25" />
          <g clipPath={`url(#${id}-clip)`}>
            <g className={`origin-center [transform-box:fill-box] motion-safe:animate-teammate-blink ${face.blinkDelay}`}>
              {lively ? <TeammateLivelyEyes face={face} head={body.head} /> : eyeArt}
            </g>
          </g>
        </g>
      </svg>
      {working && (
        <span
          className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ${ringClassName}`}
        />
      )}
    </div>
  );
}
